import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Printer, Download, BookmarkPlus, ChevronLeft } from 'lucide-react'
import { Separator } from '@/shared/components/ui/separator'
import { Badge } from '@/shared/components/ui/badge'
import { usePageMeta } from '@/shared/lib/context/page-meta-context'
import { useBreadcrumbs } from '@/shared/hooks/use-breadcrumbs'
import { Breadcrumb } from './breadcrumb'
import { ToolbarActionButton } from './toolbar-action-button'

export function SecondaryToolbar() {
  const { t } = useTranslation()
  const meta = usePageMeta()
  const breadcrumbs = useBreadcrumbs()

  // Derive display title: from page meta or last breadcrumb segment
  const displayTitle = meta?.title ?? breadcrumbs[breadcrumbs.length - 1]?.label

  return (
    <div
      className="sticky top-14 z-40 h-10 px-4 flex items-center justify-between border-b border-toolbar-border"
      style={{
        background: 'linear-gradient(to right, hsl(var(--toolbar)), hsl(var(--toolbar-end)))',
      }}
    >
      {/* Left: title + breadcrumb */}
      <div className="flex items-center gap-3 min-w-0">
        {meta?.back && (
          <>
            <Link
              to={meta.back.to}
              className="inline-flex items-center gap-1 text-xs text-toolbar-foreground/80 hover:text-toolbar-foreground transition-colors shrink-0"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {meta.back.label}
            </Link>
            <Separator orientation="vertical" className="h-4 bg-toolbar-muted/30" />
          </>
        )}
        {displayTitle && (
          <>
            <h1 className="text-sm font-semibold text-toolbar-foreground truncate shrink-0">
              {displayTitle}
            </h1>
            {breadcrumbs.length > 0 && (
              <>
                <Separator orientation="vertical" className="h-4 bg-toolbar-muted/30" />
                <Breadcrumb segments={breadcrumbs} />
              </>
            )}
          </>
        )}
      </div>

      {/* Right: page actions + common actions */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Demo badge */}
        {meta?.demo && (
          <>
            <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px] px-1.5 py-0 h-4 font-medium">
              Demo
            </Badge>
            <Separator orientation="vertical" className="h-4 mx-1 bg-toolbar-muted/30" />
          </>
        )}

        {/* Page-specific actions from context */}
        {meta?.actions && (
          <>
            {meta.actions}
            <Separator orientation="vertical" className="h-4 mx-1 bg-toolbar-muted/30" />
          </>
        )}

        {/* Common actions — only shown when the page provides handlers */}
        {meta?.onSaveView && (
          <ToolbarActionButton
            icon={BookmarkPlus}
            label={t('toolbar.saveView')}
            onClick={meta.onSaveView}
          />
        )}
        {meta?.onExport && (
          <ToolbarActionButton
            icon={Download}
            label={t('toolbar.export')}
            onClick={meta.onExport}
          />
        )}
        <ToolbarActionButton
          icon={Printer}
          label={t('toolbar.print')}
          onClick={() => window.print()}
        />
      </div>
    </div>
  )
}
